import {
  useAddSlidesToGroupMutation,
  useCreateSlideGroupMutation,
  useDeleteSlideFromGroupMutation,
  useDeleteSlideGroupMutation,
  useGetSlideGroupsQuery,
  useUpdateSlideGroupMutation,
} from 'store/apis/slideGroupsApi';
import { formatSlideGroupName } from 'utilities/groups';
import { useSnackbar } from 'utilities/hooks/useSnackbar/useSnackbar';
import { useEffect, useState } from 'react';
import { serializeNameAndUUID } from 'utilities/api';
import { FETCH_TEAMS_ERROR_MESSAGE } from 'constants/errorMessages';
import { useUnmountIgnore } from 'utilities/useUnmountIgnore';
import { useLazyGetTeamsQuery } from 'store/apis/teamsApi';

export const useCreateSlideGroup = () => {
  const [createSlideGroupMutation, { isLoading }] =
    useCreateSlideGroupMutation();
  const { showSuccess, showError } = useSnackbar();

  const createSlideGroup = (slideGroupName, team) => {
    const slideGroupNameSerialized = slideGroupName.trim();
    const teamUUID = team.value;

    const postParams = {
      name: slideGroupNameSerialized,
      team_uuid: teamUUID,
    };

    return createSlideGroupMutation(postParams)
      .unwrap()
      .then(() =>
        showSuccess(`Slide Group ${slideGroupNameSerialized} created!`),
      )
      .catch((e) => {
        const errorMessage = e.response?.data;
        const errorMessageToDisplay = JSON.stringify(errorMessage);
        showError(errorMessageToDisplay);
      });
  };

  return {
    createSlideGroup,
    isLoading,
  };
};

export const useDeleteSlideGroup = () => {
  const [deleteSlideGroupMutation, { isLoading }] =
    useDeleteSlideGroupMutation();
  const { showSuccess, showError } = useSnackbar();

  const deleteSlideGroup = (slideGroup) =>
    deleteSlideGroupMutation(slideGroup.uuid)
      .unwrap()
      .then(() => showSuccess('Slides group was successfully removed'))
      .catch(() => showError('Error occurred during removing slide group'));

  return {
    deleteSlideGroup,
    isLoading,
  };
};

export const useDeleteSlidesFromSlideGroup = () => {
  const [deleteSlideFromGroup, { isLoading }] =
    useDeleteSlideFromGroupMutation();
  const { showSuccess, showError } = useSnackbar();

  const deleteSlidesFromSlideGroup = (slideGroup, slides, isFavorite) => {
    const postParams = Array.isArray(slides)
      ? {
        slide_uuids: slides.map((slide) => slide.uuid),
      }
      : {
        slide_uuid: slides.uuid,
      };
    const groupName = formatSlideGroupName(slideGroup.name);

    deleteSlideFromGroup({ slideGroupUUID: slideGroup.uuid, postParams })
      .unwrap()
      .then(() =>
        showSuccess(
          isFavorite
            ? 'Slides were successfully removed from favorites'
            : `Slides were successfully removed from ${groupName} slide group`,
        ),
      )
      .catch(() =>
        showError('Error occurred during removing slides from group'),
      );
  };

  return {
    deleteSlidesFromSlideGroup,
    isLoading,
  };
};

export const useAddSlideToGroup = () => {
  const { showSuccess, showError } = useSnackbar();
  const [addSlidesToGroup, { isLoading }] = useAddSlidesToGroupMutation();

  const addSlideToSlideGroup = (slideGroup, slides, isFavorite) => {
    const postParams = {
      slide_uuids: slides.map((slide) => slide.uuid),
    };
    const groupName = formatSlideGroupName(slideGroup.name);

    addSlidesToGroup({ slideGroupUUID: slideGroup.uuid, postParams })
      .unwrap()
      .then(() =>
        showSuccess(
          isFavorite
            ? 'Slides were successfully added to favorites'
            : `Slides were successfully added to ${groupName} slide group`,
        ),
      )
      .catch(() => showError('Error occurred during adding slides to group'));
  };

  return {
    addSlideToSlideGroup,
    isLoading,
  };
};

export const useRenameSlideGroup = () => {
  const { showSuccess, showError } = useSnackbar();
  const [updateSlideGroup] = useUpdateSlideGroupMutation();
  const { data: allSideGroups } = useGetSlideGroupsQuery();

  const renameSlideGroup = (slideGroup, newName) => {
    const isEqualName = slideGroup.name === newName.trim();

    if (isEqualName) {
      return Promise.resolve();
    }

    const isSlideGroupWithUpdatedNameExist = !!allSideGroups.find(
      (group) => group.name === newName,
    );

    if (isSlideGroupWithUpdatedNameExist) {
      showError('Group with such name already exists!');

      return null;
    }

    const postParams = {
      ...slideGroup,
      name: newName,
    };

    return updateSlideGroup({ slideGroupUUID: slideGroup.uuid, postParams })
      .unwrap()
      .then(() => {
        showSuccess(`Group name was changed to ${newName}`);
      })
      .catch(() => showError('Error occurred during renaming slide group'));
  };

  return {
    renameSlideGroup,
  };
};

export const useGetTeams = (setValue) => {
  const unmountIgnore = useUnmountIgnore();

  const { showError } = useSnackbar();
  const [getTeams] = useLazyGetTeamsQuery();

  const [team, setTeam] = useState();
  const [selectableTeams, setSelectableTeams] = useState([]);

  useEffect(() => {
    getTeams()
      .unwrap()
      .then((response) => {
        const selectableTeamsValues = response.map(serializeNameAndUUID);

        // this is used to default to a primary team when someone is making their order (most users will probably only have one team)
        const teamValue = serializeNameAndUUID(response[0]);
        if (!unmountIgnore.current) {
          setTeam(teamValue);
          setSelectableTeams(selectableTeamsValues);
          setValue('selectedTeam', teamValue);
        }
      })
      .catch(() => showError(FETCH_TEAMS_ERROR_MESSAGE));
  }, [unmountIgnore]);

  return {
    team,
    selectableTeams,
  };
};
